// Golden-hour colour grade, applied in scene-referred light just before tone mapping.
// AgX keeps highlights natural but mutes saturated sunset colour; this restores the amber and
// rose of the ui/index.md direction with a little saturation, contrast and a warm gain, while
// keeping shadows slightly cool so black fur stays readable. Moods can be swapped later
// (race, podium, trip) by changing the uniforms.
import { Uniform, Vector3 } from 'three';
import { BlendFunction, Effect } from 'postprocessing';

const fragment = /* glsl */ `
uniform float saturation;
uniform float contrast;
uniform vec3 gain;
uniform vec3 shadowTint;
void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec3 c = max(inputColor.rgb, 0.0);
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = mix(vec3(l), c, saturation);
  c = pow(max(c, 1e-5) / 0.18, vec3(contrast)) * 0.18;
  float shadow = 1.0 - smoothstep(0.0, 0.12, l);
  c *= mix(gain, shadowTint, shadow);
  outputColor = vec4(c, inputColor.a);
}`;

export interface GradeMood { saturation: number; contrast: number; gain: [number, number, number]; shadowTint: [number, number, number] }

export const GOLDEN_HOUR: GradeMood = { saturation: 1.32, contrast: 1.1, gain: [1.06, 0.99, 0.9], shadowTint: [0.93, 0.97, 1.08] };

export class GradeEffect extends Effect {
  constructor(mood: GradeMood = GOLDEN_HOUR) {
    super('GoldenHourGrade', fragment, {
      blendFunction: BlendFunction.SRC,
      uniforms: new Map<string, Uniform>([
        ['saturation', new Uniform(mood.saturation)],
        ['contrast', new Uniform(mood.contrast)],
        ['gain', new Uniform(new Vector3(...mood.gain))],
        ['shadowTint', new Uniform(new Vector3(...mood.shadowTint))],
      ]),
    });
  }

  setMood(m: GradeMood): void {
    this.uniforms.get('saturation')!.value = m.saturation;
    this.uniforms.get('contrast')!.value = m.contrast;
    (this.uniforms.get('gain')!.value as Vector3).set(...m.gain);
    (this.uniforms.get('shadowTint')!.value as Vector3).set(...m.shadowTint);
  }
}
